import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Minus, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryLabels = {
  candles: 'Candles',
  wax_melts: 'Wax Melts',
  room_sprays: 'Room & Auto Sprays',
  skincare: 'Skincare',
  lipgloss: 'Lip Gloss',
};

export default function ProductPage() {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const res = await axios.get(`${API}/products/${id}`);
        setProduct(res.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-28 flex justify-center">
        <div className="w-8 h-8 border-2 border-[#C4A35A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-28 text-center">
        <p className="text-[#9A8E7A]">Product not found</p>
        <Link to="/shop" className="text-[#C4A35A] text-sm mt-4 inline-block">Back to Shop</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-28 sm:pt-32 pb-16" data-testid="product-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-8 text-sm">
          <Link to="/shop" className="text-[#9A8E7A] hover:text-[#C4A35A] transition-colors flex items-center gap-1" data-testid="back-to-shop">
            <ArrowLeft size={14} /> Shop
          </Link>
          <span className="text-[#2A2520]">/</span>
          <Link to={`/shop?category=${product.category}`} className="text-[#9A8E7A] hover:text-[#C4A35A] transition-colors">
            {categoryLabels[product.category] || product.category}
          </Link>
          <span className="text-[#2A2520]">/</span>
          <span className="text-[#F0E6D0]">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Images */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
            <div className="aspect-square overflow-hidden rounded-lg bg-[#141414] border border-[#2A2520] mb-4">
              {product.images && product.images[selectedImage] ? (
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-6xl font-['Cormorant_Garamond'] text-[#C4A35A]/20">CC</span>
                </div>
              )}
            </div>
            {product.images && product.images.length > 1 && (
              <div className="flex gap-3" data-testid="product-thumbnails">
                {product.images.map((img, idx) => (
                  <button
                    key={`thumb-${product.id}-${idx}`}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-16 h-16 sm:w-20 sm:h-20 rounded-md overflow-hidden border-2 transition-all ${
                      selectedImage === idx ? 'border-[#C4A35A]' : 'border-[#2A2520] hover:border-[#C4A35A]/50'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="py-4"
          >
            <p className="uppercase text-xs tracking-[0.2em] text-[#C4A35A] font-medium mb-2">{categoryLabels[product.category] || product.category}</p>
            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl text-[#E8D5A3] mb-4 leading-tight" data-testid="product-title">
              {product.name}
            </h1>
            <p className="text-2xl font-['Cormorant_Garamond'] font-semibold text-[#C4A35A] mb-6" data-testid="product-detail-price">
              ${product.price.toFixed(2)}
            </p>

            <p className="text-sm text-[#9A8E7A] leading-relaxed mb-8" data-testid="product-description">
              {product.description}
            </p>

            {product.in_stock ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-[#9A8E7A]">Quantity</span>
                  <div className="flex items-center border border-[#2A2520] rounded-full">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 flex items-center justify-center hover:bg-[#1E1E1E] rounded-l-full transition-colors text-[#9A8E7A]"
                      data-testid="product-decrease-qty"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-10 text-center text-sm font-medium text-[#F0E6D0]" data-testid="product-quantity">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 flex items-center justify-center hover:bg-[#1E1E1E] rounded-r-full transition-colors text-[#9A8E7A]"
                      data-testid="product-increase-qty"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>

                <Button
                  onClick={() => addToCart(product.id, quantity)}
                  className="w-full bg-[#C4A35A] hover:bg-[#D4B86A] text-[#0A0A0A] rounded-full h-12 text-sm font-bold"
                  data-testid="product-add-to-cart"
                >
                  Add to Bag - ${(product.price * quantity).toFixed(2)}
                </Button>
              </div>
            ) : (
              <div className="bg-[#141414] border border-[#2A2520] rounded-lg p-4 text-center">
                <p className="text-sm text-[#9A8E7A]">This product is currently sold out.</p>
              </div>
            )}

            {/* Extra Info */}
            <div className="mt-10 pt-8 border-t border-[#2A2520] space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C4A35A] mt-2 flex-shrink-0" />
                <p className="text-xs text-[#9A8E7A]">Hand-poured with premium soy, coconut-apricot, or white beeswax blends</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C4A35A] mt-2 flex-shrink-0" />
                <p className="text-xs text-[#9A8E7A]">ECO cotton and wooden crackling wicks for a clean, even burn</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C4A35A] mt-2 flex-shrink-0" />
                <p className="text-xs text-[#9A8E7A]">Fast nationwide shipping from Egg Harbor Township, NJ</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
